import {
  createContext,
  useCallback,
  useContext,
  useRef,
  type ReactNode,
} from 'react';
import { usePolling } from '@/hooks/usePolling';
import { useToast } from '@/hooks/useToast';
import { Toast } from '@/components/layout/Toast';
import { publishJobs as publishJobsApi } from '@/api/publishJobs';
import { articles as articlesApi } from '@/api/articles';
import type { PublishJobCounts, PublishJobList, PublishJobView } from '@/types';

interface PublishQueueValue {
  counts: PublishJobCounts;
  jobs: PublishJobView[];
  refresh: () => void;
}

const EMPTY_COUNTS: PublishJobCounts = { queued: 0, publishing: 0, in_flight: 0 };

const PublishQueueContext = createContext<PublishQueueValue>({
  counts: EMPTY_COUNTS,
  jobs: [],
  refresh: () => {},
});

// usePublishQueue exposes the live global in-flight publish count + rows. Any
// component under the provider (the sidebar badge, the queue page) reads from
// the single app-level poll instead of polling itself.
// eslint-disable-next-line react-refresh/only-export-components
export function usePublishQueue(): PublishQueueValue {
  return useContext(PublishQueueContext);
}

const POLL_MS = 10_000;

// PublishQueueProvider is the ONE app-level poll of the publish queue. Because
// it is mounted in the shell (not a page), its completion toasts survive
// navigation — the thing that is page-local and lost today. It watches the set
// of in-flight jobs and, when one leaves the active set, fires a "Published" /
// "didn't complete" toast keyed to the article's final status.
export function PublishQueueProvider({ children }: { children: ReactNode }): JSX.Element {
  const toast = useToast();
  // jobId -> { articleId, title, siteSlug } for every job seen in flight.
  const tracked = useRef<Map<string, { articleId: string; title: string; siteSlug: string }>>(
    new Map(),
  );

  const onTransition = useCallback(
    (prev: PublishJobList | null, next: PublishJobList) => {
      const nextIds = new Set(next.jobs.map((j) => j.id));
      for (const j of next.jobs) {
        if (!tracked.current.has(j.id)) {
          tracked.current.set(j.id, {
            articleId: j.article_id,
            title: j.article_title || j.article_slug || 'article',
            siteSlug: j.site_slug,
          });
        }
      }
      // First successful load only seeds the tracked set — never toasts a
      // publish that was already in flight before this tab opened.
      if (prev === null) return;
      for (const [jobId, info] of Array.from(tracked.current.entries())) {
        if (nextIds.has(jobId)) continue;
        tracked.current.delete(jobId);
        // The job left the active set — resolve the article's final status to
        // decide the toast. A live article published; anything else didn't
        // complete (failed, blocked, or cancelled).
        void articlesApi
          .get(info.articleId)
          .then((a) => {
            // A job leaves the active set when it succeeds (article live, or
            // still 'publishing' while Cloudflare finishes the deploy) OR fails
            // (rolled back to draft). Treat live/publishing as success so a slow
            // CF build (BatchInFlight) isn't mis-reported as a failure.
            if (a.status === 'live' || a.status === 'publishing') {
              toast.push(
                {
                  text: `Published: ${info.title}`,
                  href: `/sites/${info.siteSlug}/articles/${info.articleId}`,
                  linkLabel: 'Open',
                },
                'success',
              );
            } else {
              const sub = a.status === 'draft' ? 'drafts' : 'articles';
              toast.push(
                {
                  text: `Publish didn't complete: ${info.title}`,
                  href: `/sites/${info.siteSlug}/${sub}/${info.articleId}`,
                  linkLabel: 'Open',
                },
                'warning',
              );
            }
          })
          .catch(() => {
            /* transient — the next poll or the article page will reconcile */
          });
      }
    },
    [toast],
  );

  const { data, refresh } = usePolling<PublishJobList>(
    () => publishJobsApi.listGlobal(),
    { intervalMs: POLL_MS, enabled: true, onTransition },
  );

  return (
    <PublishQueueContext.Provider
      value={{ counts: data?.counts ?? EMPTY_COUNTS, jobs: data?.jobs ?? [], refresh }}
    >
      {children}
      <Toast messages={toast.messages} onDismiss={toast.dismiss} />
    </PublishQueueContext.Provider>
  );
}
